import { Group, Loader, Paper, Stack, Text } from '@mantine/core';
import { AvatarMobile } from 'components/AvatarMobile';
import { Nothing } from 'components/Nothing';
import styles from './EmployedsListMobile.module.scss';
import { FC, useState } from 'react';
import { getFormatPhone } from 'utils/getFotmatPhone';
import { useNavigate } from 'react-router-dom';
import { Employed } from 'redux/Api/employedsApi/type';
import { useDownloadContractMutation } from 'redux/Api/contractsApi/contractsApi';
import { TextWithTooltip } from 'hooks/TextWithTooltip';
import classNames from 'classnames';
import { EmployedProblem } from 'pages/EmployedsContent/utils';
import { EmployedsСategory } from 'components/EmployedsСategory';
import { EmployedsCategoryDrawer } from 'components/EmployedsСategory/EmployedsCategoryDrawer';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useAssignCategoryToWorkerMutation } from 'redux/Api/employedsApi/employedsApi';
import { statusIcons, statusText } from 'ui/EmployedItem';
import { ContractStatus } from 'redux/Api/contractsApi/type';
import { statusColor } from 'components/ContractsListMobile';

interface EmployedsListTypes {
  data: Employed[];
  intersectionComponent: JSX.Element | null;
  activeTab: string;
  setIsCreatedModalOpen: (value: boolean) => void;
}

export const EmployedsListMobile: FC<EmployedsListTypes> = ({
  data,
  intersectionComponent,
  setIsCreatedModalOpen,
}) => {
  const navigate = useNavigate();
  const [downloadingContractId, setDownloadingContractId] = useState<number | null>(null);
  const reduxEmployeds = useSelector((state: RootState) => state.employeds.items);
  const [assignCategoryToWorker] = useAssignCategoryToWorkerMutation();

  const getEmployedFromRedux = (id: number) => {
    return reduxEmployeds.find((e) => e.id === id);
  };

  const [downloadContract] = useDownloadContractMutation();
  const handleDownloadContract = async (id: number | undefined) => {
    if (!id) return;
    setDownloadingContractId(id);
    try {
      const contractId = typeof id === 'string' ? parseInt(id) : id;
      const blob = await downloadContract(contractId).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `unified_contract_${contractId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('error', error);
    } finally {
      setDownloadingContractId(null);
    }
  };

  const [openCategoryId, setOpenCategoryId] = useState<number | null>(null);

  const handleCategoryToggle = (employedId: number) => {
    setOpenCategoryId((prev) => (prev === employedId ? null : employedId));
  };

  const handleApplyCategories = async (categories: string[]) => {
    if (openCategoryId == null) return;
    try {
      await assignCategoryToWorker({
        worker_id: String(openCategoryId),
        category_id: categories,
      }).unwrap();
    } catch (error) {
      console.error('Error updating categories:', error);
    }
  };

  return (
    <Stack align='stretch' justify='center' gap='sm' mb='lg'>
      {data?.length ? (
        data.map((initialItem: Employed) => {
          const item = getEmployedFromRedux(initialItem.id) || initialItem;

          return (
            <Paper
              shadow='0'
              p='sm'
              radius='md'
              key={item.id}
              style={{ cursor: 'pointer', overflow: 'hidden' }}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                navigate(`/employees/${item.id}`);
              }}>
              <div className={styles.content}>
                <div className={styles.nameGroup}>
                  <div className={styles.name}>
                    <AvatarMobile
                      size={36}
                      name={item?.name}
                      surname={item?.surname}
                      avatar={item?.worker_app?.avatar}
                      isDeleted={item?.is_deleted}
                    />
                    <span className={styles.nameTextWrap}>
                      <div className={classNames(styles.name_text)}>
                        <TextWithTooltip
                          content={
                            !item?.surname
                              ? item?.name
                              : item?.patronymic
                              ? `${item?.surname} ${item?.name} ${item?.patronymic}`
                              : `${item?.surname} ${item?.name}`
                          }
                          className={classNames(styles.name_text)}
                          isMobile
                        />
                      </div>
                      {item.inn && <div className={styles.inn}>ИНН {item.inn}</div>}
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text
                      component='a'
                      href={`tel:+${item.phone}`}
                      fw='400'
                      fz='14px'
                      lh='18px'
                      truncate='end'>
                      {getFormatPhone(item.phone)}
                    </Text>
                    {item.worker_app?.nationality && (
                      <Group gap='8px'>
                        <div
                          style={{
                            width: '4px',
                            height: '4px',
                            backgroundColor: '#121922',
                            borderRadius: '50%',
                          }}
                        />
                        <Text fw='400' fz='14px' lh='18px' c='#121922'>
                          {item.worker_app?.nationality || ''}
                        </Text>
                      </Group>
                    )}
                  </div>
                  {item.unified_contract ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                        }}>
                        {statusIcons[item.unified_contract?.status as ContractStatus]}
                      </div>
                      <div className={styles.statusWithDate_text}>
                        <div
                          className={styles.statusText}
                          style={{
                            color:
                              statusColor[
                                item.unified_contract?.status as ContractStatus
                              ],
                          }}>
                          <div
                            style={{
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '18px',
                            }}>
                            {statusText[item.unified_contract?.status as ContractStatus]}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.noContract}>Нет договора</div>
                  )}
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    marginRight: '60px',
                  }}>
                  {item?.categories?.length > 0 && (
                    <div className={styles.categoryes}>
                      <div className={styles.textCategory}>
                        {item.categories.slice(0, 1).map((cat) => (
                          <TextWithTooltip key={cat.id} content={cat.name} />
                        ))}
                        {item?.categories?.length > 1 && (
                          <p>+{item?.categories?.length - 1}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {item?.categories?.length === 0 ? (
                    <div className={styles.addCategoryButton}>
                      <EmployedsСategory
                        activeCategories={item?.categories?.map((el) => el.id)}
                        employedId={String(item.id)}
                        setIsCreatedModalOpen={setIsCreatedModalOpen}
                        isOpen={false}
                        onToggle={() => handleCategoryToggle(item.id)}
                        showIcon={true}
                        fallbackCategories={item.categories}
                      />
                    </div>
                  ) : (
                    <div
                      className={
                        openCategoryId === item.id
                          ? styles.iconEditActive
                          : styles.iconEdit
                      }>
                      <EmployedsСategory
                        activeCategories={item?.categories?.map((el) => el.id)}
                        employedId={String(item.id)}
                        setIsCreatedModalOpen={setIsCreatedModalOpen}
                        isOpen={false}
                        onToggle={() => handleCategoryToggle(item.id)}
                        showIcon={false}
                        fallbackCategories={item.categories}
                      />
                    </div>
                  )}
                </div>

                {item.unified_contract?.status === 'signed_by_worker' && (
                  <Group>
                    <Text
                      fz='16px'
                      lh='22px'
                      c='#002CFB'
                      style={{ cursor: 'pointer' }}
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        e.stopPropagation();
                        handleDownloadContract(item?.unified_contract?.id);
                      }}>
                      Договор №{item.unified_contract.number}
                    </Text>
                    {downloadingContractId === item.unified_contract?.id && (
                      <Loader size='xs' color='#002CFB' />
                    )}
                  </Group>
                )}

                <EmployedProblem employed={item} screen='mobile' />
              </div>
            </Paper>
          );
        })
      ) : (
        <Nothing style={{ height: '320px' }} title='Ничего не найдено' />
      )}
      {intersectionComponent}
      <EmployedsCategoryDrawer
        opened={openCategoryId !== null}
        onClose={() => setOpenCategoryId(null)}
        selectedCategories={
          openCategoryId !== null
            ? (
                getEmployedFromRedux(openCategoryId) ||
                data?.find((item) => item.id === openCategoryId)
              )?.categories?.map((cat) => String(cat.id)) || []
            : []
        }
        employedId={openCategoryId !== null ? String(openCategoryId) : undefined}
        onApply={handleApplyCategories}
        setIsCreatedModalOpen={setIsCreatedModalOpen}
        fallbackCategories={
          openCategoryId !== null
            ? (
                getEmployedFromRedux(openCategoryId) ||
                data?.find((item) => item.id === openCategoryId)
              )?.categories
            : undefined
        }
      />
    </Stack>
  );
};
